import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { Icon } from "../components/Icon";
import "../styles/admin.css";

/* ===================================================================
   Shared admin primitives.

   One implementation of every pattern the admin redesign repeats:
   the section card with its own save scope, the slide-over drawer,
   the toast, and the small display atoms (pill, health dot, progress,
   avatar, callout, toggle, field). Pages compose these; nobody
   re-implements a save bar.
   =================================================================== */

/* ------------------------------------------------------------------ */
/*  Display atoms                                                      */
/* ------------------------------------------------------------------ */

export type HealthState = "ok" | "warn" | "todo";

const HEALTH_TITLE: Record<HealthState, string> = {
  ok: "Complete",
  warn: "Needs attention",
  todo: "Not set up",
};

/** A `null`/absent state means "not part of this customer's setup" (a section
 *  whose feature is switched off), so nothing is drawn rather than a dot that
 *  reads as unfinished work. */
export function HealthDot({ state }: { state: HealthState | null | undefined }) {
  if (!state) return null;
  return <span className={`ad-dot ${state}`} title={HEALTH_TITLE[state]} />;
}

export type PillKind = "ok" | "warn" | "info" | "off" | "danger";

const PILL_TONE: Record<PillKind, string> = {
  ok: "badge-success",
  warn: "badge-warning",
  info: "badge-info",
  off: "badge-neutral",
  danger: "badge-danger",
};

export function Pill({ kind, children }: { kind: PillKind; children: ReactNode }) {
  return (
    <span className={`badge ${PILL_TONE[kind]}`}>
      <span className="dot" />
      {children}
    </span>
  );
}

const AVATAR_TONES = ["avatar-purple", "avatar-teal", "avatar-green", "avatar-amber"] as const;

/** Initials avatar. Tone is derived from the name so a given org or person
 *  reads the same colour on every screen (name.length % 4, same slots as before).
 *  Slot 1 used to be called blue; that fill was teal. */
export function AdAvatar({
  name,
  size,
  className,
}: {
  name: string;
  size?: "lg";
  className?: string;
}) {
  const tone = AVATAR_TONES[name.length % 4];
  const initials =
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  // .ad-av.lg set radius 12 on its own. That radius is .avatar-lg.avatar-tile.
  // The header caller passes size="lg" and does not pass className="tile".
  const mapped = (className ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => (token === "tile" ? "avatar-tile" : token));
  const classes = [
    "avatar",
    tone,
    size === "lg" ? "avatar-lg" : "",
    size === "lg" ? "avatar-tile" : "",
    ...mapped,
  ].filter(Boolean);
  return (
    <span className={[...new Set(classes)].join(" ")} aria-hidden="true">
      {initials}
    </span>
  );
}

/** done/total setup progress. Amber fill until every section is complete. */
export function Progress({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <span className={`ad-prog${done < total ? " warn" : ""}`}>
      <span className="bar">
        <i style={{ width: `${pct}%` }} />
      </span>
      {done}/{total}
    </span>
  );
}

export type SortDir = "asc" | "desc";

/** Which column a table is ordered by, or `null` for the caller's own default
 *  order (which a third click on a heading returns to). */
export interface TableSort<K extends string> {
  key: K;
  dir: SortDir;
}

/** One sortable column heading, reusing the design system's `.th-sortable`
 *  treatment (the idle arrow only shows on hover, so a row of headings does not
 *  read as all-sorted-at-once).
 *
 *  The button, not the `th`, is the control so it is reachable by keyboard, and
 *  `aria-sort` announces the current order. */
export function SortableTh<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: K;
  sort: TableSort<K> | null;
  onSort: (key: K) => void;
}) {
  const active = sort?.key === sortKey;
  return (
    <th
      className={`th-sortable${active ? " is-sorted" : ""}`}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        className="th-sort-btn"
        title={`Sort by ${label.toLowerCase()}`}
        onClick={() => onSort(sortKey)}
      >
        {label}
        <span className="th-sort-arrow" aria-hidden="true">
          {active ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </button>
    </th>
  );
}

export function Callout({
  kind = "info",
  children,
}: {
  kind?: "info" | "ok" | "warn" | "danger";
  children: ReactNode;
}) {
  const icon = kind === "ok" ? "check" : kind === "info" ? "info" : "info";
  return (
    <div className={`ad-callout${kind === "info" ? "" : ` ${kind}`}`}>
      <Icon name={icon} />
      <span>{children}</span>
    </div>
  );
}

export function Empty({
  title,
  blurb,
  action,
}: {
  title: string;
  blurb?: string;
  action?: ReactNode;
}) {
  return (
    <div className="ad-empty">
      <div className="t">{title}</div>
      {blurb && <div className="s">{blurb}</div>}
      {action}
    </div>
  );
}

export function KeyValue({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="ad-kv">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

/** Toggle row: label + sub-copy on the left, switch on the right. A disabled
 *  row states WHY in its sub-copy (the handoff's "disabled controls are
 *  honest" rule), so callers pass a warn pill in `sub`. */
export function ToggleRow({
  label,
  sub,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  sub?: ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className={`ad-toggle${disabled ? " disabled" : ""}`}>
      <span className="ad-toggle-text">
        <span className="l">{label}</span>
        {sub && <span className="s">{sub}</span>}
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="ad-track">
        <span className="ad-knob" />
      </span>
    </label>
  );
}

/** Bare switch with no label row, for use inside a table cell or list row. */
export function Switch({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <label className={`ad-toggle${disabled ? " disabled" : ""}`} style={{ padding: 0, border: 0 }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="ad-track">
        <span className="ad-knob" />
      </span>
    </label>
  );
}

export function Field({
  label,
  hint,
  note,
  span,
  children,
}: {
  label: string;
  hint?: string;
  note?: ReactNode;
  span?: boolean;
  children: ReactNode;
}) {
  const autoId = useId();
  const { control, htmlFor } = associateFieldControl(children, autoId);
  return (
    <div className={`ad-field${span ? " span2" : ""}`}>
      <label htmlFor={htmlFor}>
        {label}
        {hint && <span className="h">{hint}</span>}
      </label>
      {control}
      {note && <div className="ad-note">{note}</div>}
    </div>
  );
}

/** Point the label at a single input, select, textarea, or chip field.
 *  Anything else keeps the previous unassociated markup. */
function associateFieldControl(
  children: ReactNode,
  autoId: string,
): { control: ReactNode; htmlFor?: string } {
  if (!isValidElement(children)) return { control: children };
  const el = children as ReactElement<{ id?: string }>;
  const existing = el.props.id;
  const domControl =
    typeof el.type === "string" &&
    (el.type === "input" || el.type === "select" || el.type === "textarea");
  if (!domControl && el.type !== ChipInput) return { control: children };
  const id = existing || autoId;
  return {
    control: existing ? children : cloneElement(el, { id }),
    htmlFor: id,
  };
}

/** Chip list with an Enter-to-add input. `onAdd`/`onRemove` commit
 *  immediately when `immediate` is set (the SSO domains case: audited on
 *  the spot, so it must not mark the section dirty). */
export function ChipInput({
  values,
  onAdd,
  onRemove,
  placeholder = "Add and press Enter",
  disabled,
  id,
}: {
  values: string[];
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}) {
  const [draft, setDraft] = useState("");
  function commit() {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    setDraft("");
    if (values.includes(v)) return;
    onAdd(v);
  }
  return (
    <>
      <div className="ad-chips">
        {values.map((v) => (
          <span key={v} className="ad-chip">
            {v}
            <button
              type="button"
              aria-label={`Remove ${v}`}
              disabled={disabled}
              onClick={() => onRemove(v)}
            >
              <Icon name="x" />
            </button>
          </span>
        ))}
      </div>
      <input
        id={id}
        style={{ marginTop: 8 }}
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={id ? undefined : placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit();
          }
        }}
        onBlur={commit}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Toast                                                              */
/* ------------------------------------------------------------------ */

type ToastFn = (message: string, kind?: "ok" | "warn") => void;

const ToastCtx = createContext<ToastFn>(() => {});

/** Wraps the admin area so any descendant can `useToast()`. One toast at a
 *  time, bottom-centre, 2.2s — matches the prototype. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<{ message: string; kind: "ok" | "warn" } | null>(
    null,
  );
  const [shown, setShown] = useState(false);
  const timers = useRef<number[]>([]);

  useEffect(
    () => () => {
      timers.current.forEach((t) => window.clearTimeout(t));
    },
    [],
  );

  const push = useCallback<ToastFn>((message, kind = "ok") => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
    setToast({ message, kind });
    setShown(true);
    timers.current.push(window.setTimeout(() => setShown(false), 2200));
    timers.current.push(window.setTimeout(() => setToast(null), 2600));
  }, []);

  return (
    <ToastCtx.Provider value={push}>
      {children}
      {toast &&
        createPortal(
          <div
            className={`ad-toast${shown ? " show" : ""}${toast.kind === "warn" ? " warn" : ""}`}
            role="status"
            aria-live="polite"
          >
            <Icon name={toast.kind === "warn" ? "info" : "check"} />
            {toast.message}
          </div>,
          document.body,
        )}
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastFn {
  return useContext(ToastCtx);
}

/* ------------------------------------------------------------------ */
/*  Section card with its own save scope                               */
/* ------------------------------------------------------------------ */

export interface SectionProps {
  title: string;
  /** Short noun for the Save button ("Save details"). Defaults to the title. */
  short?: string;
  desc?: ReactNode;
  /** Right-hand header actions. */
  actions?: ReactNode;
  /** True while the body differs from the server state. Drives the save bar. */
  dirty?: boolean;
  /** Omit both to render a card with no footer (lists, read-only explainers). */
  onSave?: () => void | Promise<void>;
  onDiscard?: () => void;
  /** Relative "Saved 3 days ago" line, shown when clean. */
  savedLabel?: string | null;
  saving?: boolean;
  error?: string | null;
  /** Removes the body padding, for a table that should bleed to the edges. */
  flush?: boolean;
  children: ReactNode;
}

export function Section({
  title,
  short,
  desc,
  actions,
  dirty = false,
  onSave,
  onDiscard,
  savedLabel,
  saving = false,
  error,
  flush = false,
  children,
}: SectionProps) {
  const hasFooter = Boolean(onSave);
  return (
    <section className={`ad-sec${dirty ? " dirty" : ""}`}>
      <header className="ad-sec-head">
        <div>
          <h2>{title}</h2>
          {desc && <p>{desc}</p>}
        </div>
        {actions && <div className="grp" style={{ display: "flex", gap: 8 }}>{actions}</div>}
      </header>
      <div className={`ad-sec-body${flush ? " flush" : ""}`}>
        {error && (
          <div className="form-error" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}
        {children}
      </div>
      {hasFooter && (
        <footer className="ad-savebar">
          {dirty ? (
            <span className="ad-dirty">
              <Icon name="info" />
              Unsaved changes in {title}
            </span>
          ) : (
            <span className="ad-savestate">
              {savedLabel ? (
                <>
                  <Icon name="check" />
                  Saved {savedLabel}
                </>
              ) : (
                <span className="faint">No changes</span>
              )}
            </span>
          )}
          <span className="grp">
            {onDiscard && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={onDiscard}
                disabled={saving}
              >
                Discard
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => void onSave?.()}
              disabled={saving}
            >
              {saving ? "Saving…" : `Save ${short ?? title}`}
            </button>
          </span>
        </footer>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Slide-over drawer                                                  */
/* ------------------------------------------------------------------ */

export function Drawer({
  eyebrow,
  title,
  desc,
  wide,
  footer,
  onClose,
  children,
}: {
  eyebrow?: string;
  title: string;
  desc?: ReactNode;
  wide?: boolean;
  footer?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const raf = requestAnimationFrame(() => setOpen(true));
    const prevOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    // Focus the panel itself rather than its first control: the first control
    // is the close button, and opening onto a focus ring on "×" reads as an
    // error. The panel carries the dialog role, so this is what gets announced.
    panelRef.current?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable =
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const items = Array.from(panel.querySelectorAll<HTMLElement>(focusable)).filter(
        (el) => el.offsetParent !== null,
      );
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      // A focused footer button that becomes disabled drops focus to the body.
      // The next Tab has to come back inside, unless a modal or slide-over
      // stacked on the drawer already owns it.
      if (!active || !panel.contains(active)) {
        if (
          active instanceof Element &&
          active !== document.body &&
          active.closest('[aria-modal="true"], .modal-backdrop, .slideover')
        ) {
          return;
        }
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || active === panel)) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  return createPortal(
    <div
      className={`ad-drawer-wrap${open ? " open" : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`ad-drawer${wide ? " wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panelRef}
      >
        <header className="ad-drawer-head">
          <div>
            {eyebrow && <div className="ad-eyebrow">{eyebrow}</div>}
            <h2>{title}</h2>
            {desc && <p>{desc}</p>}
          </div>
          <button type="button" className="ad-x" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </header>
        <div className="ad-drawer-body">{children}</div>
        {footer && <footer className="ad-drawer-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/*  Formatting helpers                                                 */
/* ------------------------------------------------------------------ */

/** "3 days ago" / "just now". AWST display, per the hard rules. */
export function relativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.round(months / 12);
  return `${years} year${years === 1 ? "" : "s"} ago`;
}

/** "Mar 2024" — the customer-list "Since" column. */
export function monthYear(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", {
    month: "short",
    year: "numeric",
    timeZone: "Australia/Perth",
  });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "Never";
  return new Date(iso).toLocaleString("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Australia/Perth",
  });
}

/** Extracts a readable message from anything thrown by the api layer. */
export function errText(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

/** Case-insensitive "does this row match the filter box" helper. */
export function useFiltered<T>(
  items: T[],
  query: string,
  fields: (item: T) => (string | null | undefined)[],
): T[] {
  return useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) =>
      fields(item).some((f) => (f ?? "").toLowerCase().includes(q)),
    );
    // `fields` is a fresh closure each render by design; the query + items are
    // what actually change the result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, query]);
}
